import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
} from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";

const themeColor = "#4B0082";

const DeviceChangeDetails = () => {
  const { setLoading } = useContext(LoaderContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("unverified");

  const firstLoadRef = useRef(true);


  const fetchUsers = async () => {
    const res = await axios.get(`${BASE_URL}/userregistration`);
    setUsers(res.data || []);
  };


  const fetchDeviceChanges = async () => {
    try {
      if (firstLoadRef.current) setLoading(true);

      const endpoint =
        statusFilter === "verified"
          ? "/devicechange/verified"
          : "/devicechange/unverified";

      const res = await axios.get(`${BASE_URL}${endpoint}`);
      const deviceList = res.data || [];


      const merged = deviceList.map((d) => {
        const user = users.find(
          (u) => u.mobileNo === d.mobileNo && u.password === d.password
        );

        return {
          ...d,
          userName: user?.userName || "-",
          email: user?.email || "-",
          mobileNo: user?.mobileNo || d.mobileNo,
        };
      });

      setDataSource(merged);
    } catch (err) {
      console.error(err);
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    }
  };


  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!users.length) return;

    fetchDeviceChanges(); 

    const interval = setInterval(() => {
      fetchDeviceChanges();
    }, 5000);

    return () => clearInterval(interval);
  }, [users, statusFilter]);

  const handleEdit = (id) => {
    navigate(`/dashboard/userdetails/${id}`);
  };

 
  const columns = useMemo(
    () => [
      {
        header: "Action",
        size: 40,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() => handleEdit(row.original.deviceChangeId)}
            sx={{
              color: themeColor,
              backgroundColor: "#ffe6e9",
              "&:hover": { backgroundColor: "#ffe6e9" },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      { accessorKey: "userName", header: "User Name", size: 150 },
      { accessorKey: "mobileNo", header: "Mobile No", size: 130 },
      { accessorKey: "email", header: "Email", size: 200 },
      { accessorKey: "deviceName", header: "Device Name", size: 150 },
      {
        accessorKey: "lastChanged",
        header: "Requested On",
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "-";
          return new Date(value).toLocaleDateString("en-GB");
        },
      },
    ],
    []
  );


  const filteredData = useMemo(() => {
    return dataSource.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(globalFilter.toLowerCase())
    );
  }, [dataSource, globalFilter]);

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper elevation={4} sx={{ p: { xs: 1, md: 2 }, borderRadius: 3 }}>
        
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 1.5,
            mb: 1.8,
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(90deg, ${themeColor}, #b86fec)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              Device Change List
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: 2.2,
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }}
        />

      
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2.5 }}
        >
          <Stack direction="row" spacing={1}>
            {[
              { label: "Verified", value: "verified" },
              { label: "Unverified", value: "unverified" },
            ].map((btn) => (
              <Button
                key={btn.value}
                size="small"
                variant={
                  statusFilter === btn.value ? "contained" : "outlined"
                }
                onClick={() => setStatusFilter(btn.value)}
                sx={{
                  borderRadius: 20,
                  px: 2.2,
                  minHeight: 32,
                  textTransform: "none",
                  backgroundColor:
                    statusFilter === btn.value ? themeColor : "#fff",
                  color: statusFilter === btn.value ? "#fff" : themeColor,
                  borderColor: themeColor,
                }}
              >
                {btn.label}
              </Button>
            ))}
          </Stack>

     
          <TextField
            size="small"
            placeholder="Search..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: themeColor, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 20,
                height: 34,
                "& fieldset": { borderColor: "#454444" },
                "&:hover fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 2px ${themeColor}22`,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 3px ${themeColor}33`,
                },
              },
            }}
          />
        </Stack>

       
        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableTopToolbar={false}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableHiding={false}
          muiTablePaperProps={{
            sx: {
              boxShadow: "none",
              mt: 0.5,
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: { xs: "55vh", sm: "none" },
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cfcfcf",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#b0b0b0",
              },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#ffe6e9",
              fontWeight: 700,
              fontSize: { xs: "12px", sm: "14px" },
              py: { xs: 0.8, sm: 1.2 },
              whiteSpace: "nowrap",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              "& td": {
                fontSize: { xs: "11px", sm: "14px" },
                py: { xs: 0.5, sm: 1 },
                whiteSpace: "nowrap",
              },
              "&:hover": {
                backgroundColor: "#fff8e1",
              },
            },
          }}
          initialState={{
            pagination: {
              pageSize: 50,
              pageIndex: 0,
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default DeviceChangeDetails;
